import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Zap, Check, X, ChevronDown, ChevronRight, Paperclip, ExternalLink, Mail, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { appInboxQueueApprove, appInboxQueueReject } from "@/lib/api";
import { INTENT_TONE, STATUS_TONE, fmtDate, fmtKb, StatusSelect, ApplySuggestedBtn, AttachmentActionIcon, AttachmentPreviewDialog } from "./inboxShared";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const GROUPS = [
  { id: "new", label: "New applications", test: (i) => ["create_app", "request_pack"].includes(i.action) },
  { id: "update", label: "Updates to existing deals", test: (i) => ["post_update", "ob_connected"].includes(i.action) },
  { id: "other", label: "Other & forwards", test: () => true },
];

const TONE = {
  green: "bg-green-50 text-green-800 border-green-300",
  amber: "bg-amber-50 text-amber-800 border-amber-300",
  rose: "bg-rose-50 text-rose-800 border-rose-300",
  red: "bg-red-50 text-red-700 border-red-300",
  slate: "bg-slate-100 text-slate-600 border-slate-300",
  fuchsia: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300",
};

// Headline reason for "why is this waiting for me?" — derived from the autopilot blockers.
const REASON_RULES = [
  [/credit-approved deal/i, "Sales to action — credit approved deal", "fuchsia", false],
  [/possible applications/i, "Pick the application", "amber", true],
  [/no matching application/i, "No matching app", "red", true],
  [/duplicate detected/i, "Possible duplicate — confirm", "rose", true],
  [/same customer email/i, "Same client email — check", "rose", true],
  [/cancellation needs manual/i, "Cancellation — set status", "amber", true],
  [/no company name/i, "No company name", "red", true],
  [/none could be read/i, "Attachments unreadable", "amber", true],
  [/below the .* threshold/i, "Low confidence", "amber", false],
  [/limited to new applications/i, "Not a new app — manual approval", "slate", false],
  [/always need human review/i, "Always human-reviewed", "slate", false],
  [/'Other' email/i, "Other email — check proposal", "slate", false],
];

export const reasonsFor = (item) => {
  const out = [];
  for (const b of item.blockers || []) {
    const rule = REASON_RULES.find(([re]) => re.test(b));
    out.push(rule ? { label: rule[1] === "Low confidence" ? `Low confidence ${item.confidence}%` : rule[1], tone: rule[2], decision: rule[3], raw: b }
                  : { label: b, tone: "slate", decision: false, raw: b });
  }
  if (item.status === "failed") out.unshift({ label: "Failed — retry", tone: "red", decision: true, raw: item.error || "" });
  // Sales ownership is the headline whenever it applies (Dean, 9 Oct 2026)
  const sales = out.findIndex(r => r.tone === "fuchsia");
  if (sales > 0) out.unshift(out.splice(sales, 1)[0]);
  return out;
};

export const defaultsFor = (item) => {
  const matches = item.matches || [];
  const live = matches.filter(m => m.is_live && m.item_id);
  const picked = item.payload?.item_id || (live.length === 1 ? live[0].item_id : "");
  const pickedMatch = matches.find(m => m.item_id === picked);
  const funded = (pickedMatch?.status || "") === "Funded";
  const setStatus = funded ? "" : (item.payload?.suggested_status || item.payload?.set_status || "");
  return { picked, setStatus, attSel: item.payload?.attachment_ids || [] };
};

const needsPick = (item, ed) => ["post_update", "ob_connected"].includes(item.action) && !ed.picked;

export const isReady = (item, ed) =>
  item.status === "proposed" && !["review", "mark_duplicate"].includes(item.action)
  && !needsPick(item, ed) && !reasonsFor(item).some(r => r.decision);

const overrideFor = (item, ed) => {
  const o = { attachment_ids: ed.attSel };
  if (ed.picked && ed.picked !== item.payload?.item_id) o.item_id = ed.picked;
  if (item.action === "post_update") o.set_status = ed.setStatus;
  return o;
};

const subjectOf = (item, ed) => {
  if (item.action === "create_app") {
    return { sf: item.payload?.sf_number ? `SF-${item.payload.sf_number}` : "New", name: item.payload?.company_name || "(unknown company)" };
  }
  if (item.action === "request_pack") {
    return { sf: "Spark", name: item.payload?.company_name || "(unknown company)" };
  }
  const m = (item.matches || []).find(x => x.item_id === ed.picked);
  return { sf: m?.sf || item.payload?.sf || "—", name: m?.name || "" };
};

const brokerOf = (item) => item.payload?.broker_name || (item.from_email || "").split("@")[0] || "";

const approveToast = (item, r) => {
  (r.result?.warnings || []).forEach(w => toast.warning(w));
  if (r.result?.condition_alert) toast.info("Offer condition received — alert emailed to Paul");
  if (r.result?.ack?.sent) toast.success(`Acknowledgement sent to ${r.result.ack.to}`);
  toast.success(item.action === "create_app" ? `${r.result?.sf_number || "App"} created on Monday`
    : item.action === "post_update" ? `Update posted${r.result?.status_set ? ` · status → ${r.result.status_set}` : ""}`
    : item.action === "ob_connected" ? "Open Banking marked Connected"
    : item.action === "forward_email" ? `Forwarded to ${(r.result?.forwarded_to || []).join(", ")}`
    : item.action === "request_pack" ? `Pack requested from ${r.result?.to || "the sender"} (Spark link)`
    : "Email moved to Done");
};

export function NeedsReviewTable({ queue, mode, running, onRun, onChanged, selectedId, onSelect, readingPane }) {
  const [view, setView] = useState("pending");
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const items = queue.items || [];
  const pending = useMemo(() => items.filter(i => ["proposed", "failed"].includes(i.status))
    .sort((a, b) => String(a.received || "").localeCompare(String(b.received || ""))), [items]);
  const done = items.filter(i => !["proposed", "failed"].includes(i.status)).slice(0, 60);
  const edFor = (item) => edits[item.queue_id] || defaultsFor(item);
  const setEd = (item, patch) => setEdits(prev => ({ ...prev, [item.queue_id]: { ...edFor(item), ...patch } }));
  const ready = pending.filter(i => isReady(i, edFor(i)));

  const grouped = GROUPS.map(g => ({ ...g, rows: [] }));
  pending.forEach(i => grouped.find(g => g.test(i)).rows.push(i));

  const approve = async (item) => {
    setBusy(item.queue_id + ":approve");
    try { approveToast(item, await appInboxQueueApprove(item.queue_id, overrideFor(item, edFor(item)))); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); }
    finally { setBusy(null); await onChanged(); }
  };
  const reject = async (item) => {
    setBusy(item.queue_id + ":reject");
    try { await appInboxQueueReject(item.queue_id); toast.success("Rejected — the email stays in the inbox for manual handling"); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); }
    finally { setBusy(null); await onChanged(); }
  };
  const approveAll = async () => {
    setBulkOpen(false);
    setBusy("bulk");
    let ok = 0; const failed = [];
    for (const item of ready) {
      try { await appInboxQueueApprove(item.queue_id, overrideFor(item, edFor(item))); ok++; }
      catch (e) { failed.push(`${subjectOf(item, edFor(item)).sf}: ${e.response?.data?.detail || e.message}`); }
    }
    setBusy(null);
    toast.success(`Approved ${ok} of ${ready.length}`);
    failed.forEach(f => toast.error(f));
    await onChanged();
  };

  return (
    <div data-testid="needs-review-table">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50 flex-wrap">
        <div className="inline-flex rounded-md border overflow-hidden shrink-0">
          <button type="button" onClick={() => setView("pending")} className={cn("px-2.5 py-1 text-[11px]", view === "pending" ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50")} data-testid="queue-view-pending">
            Needs review <span className="font-mono">{pending.length}</span>
          </button>
          <button type="button" onClick={() => setView("recent")} className={cn("px-2.5 py-1 text-[11px]", view === "recent" ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50")} data-testid="queue-view-recent">
            Recent activity <span className="font-mono">{done.length}</span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 hidden xl:inline" title={mode === "auto" ? "Autopilot handles clean emails — low-confidence or ambiguous ones wait here" : "Autopilot is off — anything left here needs handling from the Inbox tab"}>
            {ready.length} ready · {pending.length - ready.length} need a decision
          </span>
          {view === "pending" && ready.length > 0 && (
            <Button size="sm" className="h-6 text-[10px] px-2 bg-green-700 hover:bg-green-800" onClick={() => setBulkOpen(true)} disabled={!!busy} data-testid="queue-approve-all-btn">
              {busy === "bulk" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCheck className="w-3 h-3 mr-1" />}
              Approve all ready ({ready.length})
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 shrink-0" onClick={onRun} disabled={running} data-testid="queue-scan-now-btn">
            {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1 text-amber-500" />}
            Scan now
          </Button>
        </div>
      </div>

      {view === "pending" && pending.length === 0 && (
        <div className="p-8 text-center text-xs text-muted-foreground" data-testid="queue-empty">Nothing needs review.</div>
      )}
      {view === "pending" && pending.length > 0 && (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-xs table-fixed">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 bg-white border-b">
            <tr>
              <th className="w-6" />
              <th className="text-left font-medium px-2 py-1.5 w-[190px]">Reason</th>
              <th className="text-left font-medium px-2 py-1.5 w-[130px]">Type</th>
              <th className="text-left font-medium px-2 py-1.5 w-[260px]">SF / Business</th>
              <th className="text-left font-medium px-2 py-1.5 w-[130px]">Broker</th>
              <th className="text-left font-medium px-2 py-1.5 w-[64px]">Received</th>
              <th className="text-left font-medium px-2 py-1.5">Proposed action</th>
              <th className="text-right font-medium px-2 py-1.5 w-[170px]">Decision</th>
            </tr>
          </thead>
          {grouped.filter(g => g.rows.length).map(g => (
            <tbody key={g.id} data-testid={`queue-group-${g.id}`}>
              <tr className="bg-slate-50 border-y">
                <td colSpan={8} className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                  {g.label} <span className="font-mono text-slate-500">({g.rows.length})</span>
                </td>
              </tr>
              {g.rows.map(item => (
                <ReviewRow key={item.queue_id} item={item} ed={edFor(item)} setEd={(p) => setEd(item, p)}
                  open={selectedId === item.message_id} onToggle={() => onSelect(selectedId === item.message_id ? null : item.message_id)}
                  busy={busy} onApprove={() => approve(item)} onReject={() => reject(item)}
                  readingPane={selectedId === item.message_id ? readingPane : null} />
              ))}
            </tbody>
          ))}
        </table>
        </div>
      )}

      {view === "recent" && <RecentActivity done={done} />}

      {bulkOpen && (
        <Dialog open onOpenChange={(v) => !v && setBulkOpen(false)}>
          <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="approve-all-dialog">
            <DialogHeader><DialogTitle className="text-sm">Approve {ready.length} ready item{ready.length === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
            <div className="max-h-[50vh] overflow-y-auto divide-y border rounded text-[11px]">
              {ready.map(i => {
                const s = subjectOf(i, edFor(i));
                return (
                  <div key={i.queue_id} className="px-2 py-1.5" data-testid={`approve-all-row-${i.queue_id}`}>
                    <div className="font-semibold"><span className="font-mono">{s.sf}</span> {s.name}</div>
                    <div className="text-muted-foreground truncate">{i.detail}</div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(false)} data-testid="approve-all-cancel">Cancel</Button>
              <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={approveAll} data-testid="approve-all-confirm">
                <CheckCheck className="w-3.5 h-3.5 mr-1" />Approve all
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ReviewRow({ item, ed, setEd, open, onToggle, busy, onApprove, onReject, readingPane }) {
  const reasons = reasonsFor(item);
  const head = reasons[0] || { label: "Ready", tone: "green" };
  const s = subjectOf(item, ed);
  const isReview = item.action === "review";
  const pick = needsPick(item, ed);
  const rowBusy = busy === `${item.queue_id}:approve` || busy === `${item.queue_id}:reject` || busy === "bulk";
  return (
    <>
      <tr className={cn("border-b hover:bg-slate-50 cursor-pointer align-top", open && "bg-blue-50/60")} onClick={onToggle} data-testid={`queue-row-${item.queue_id}`}>
        <td className="px-1 py-2 text-slate-400">{open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</td>
        <td className="px-2 py-2">
          <Badge variant="outline" className={cn("text-[9px] whitespace-nowrap", TONE[head.tone])} title={head.raw} data-testid={`queue-reason-${item.queue_id}`}>{head.label}</Badge>
          {reasons.length > 1 && <span className="ml-1 text-[9px] text-slate-500" title={reasons.slice(1).map(r => r.label).join("\n")}>+{reasons.length - 1}</span>}
        </td>
        <td className="px-2 py-2"><Badge variant="outline" className={cn("text-[9px] whitespace-nowrap", INTENT_TONE[item.intent] || INTENT_TONE.Other)}>{item.intent} · {item.confidence}%</Badge></td>
        <td className="px-2 py-2 min-w-0">
          <div className="font-semibold truncate"><span className="font-mono">{s.sf}</span> {s.name && <span className="font-normal">— {s.name}</span>}</div>
          <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1"><Mail className="w-2.5 h-2.5 shrink-0" />{item.subject}</div>
        </td>
        <td className="px-2 py-2 truncate text-slate-600" title={item.from_email}>{brokerOf(item)}</td>
        <td className="px-2 py-2 text-[10px] text-muted-foreground whitespace-nowrap">{fmtDate(item.received)}</td>
        <td className="px-2 py-2 min-w-0">
          <div className="line-clamp-2" title={item.detail}>{item.detail}</div>
          {item.action === "post_update" && ed.setStatus && <div className="text-[10px] text-slate-500">status → <b>{ed.setStatus}</b></div>}
        </td>
        <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          {isReview ? <span className="text-[10px] text-muted-foreground">Open ↓ &amp; use email actions</span> : (
            <Button size="sm" className={cn("h-6 text-[10px] px-2.5", item.action === "mark_duplicate" ? "bg-rose-700 hover:bg-rose-800" : "bg-green-700 hover:bg-green-800")}
              onClick={onApprove} disabled={rowBusy || pick} title={pick ? "Pick the application first (expand the row)" : ""} data-testid={`queue-approve-${item.queue_id}`}>
              {busy === `${item.queue_id}:approve` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
              {item.status === "failed" ? "Retry" : item.action === "mark_duplicate" ? "Confirm dup" : "Approve"}
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 ml-1" onClick={onReject} disabled={rowBusy} data-testid={`queue-reject-${item.queue_id}`}>
            {busy === `${item.queue_id}:reject` ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
          </Button>
        </td>
      </tr>
      {open && (
        <tr className="border-b bg-white" data-testid={`queue-expanded-${item.queue_id}`}>
          <td colSpan={8} className="p-3">
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-3 items-start">
              <div className="xl:col-span-2 space-y-2"><ReviewDetails item={item} ed={ed} setEd={setEd} reasons={reasons} /></div>
              <div className="xl:col-span-3 border rounded-md bg-white min-h-[200px]">{readingPane}</div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ReviewDetails({ item, ed, setEd, reasons }) {
  const [showAll, setShowAll] = useState(false);
  const [previewAtt, setPreviewAtt] = useState(null);
  const matches = item.matches || [];
  const live = matches.filter(m => m.is_live);
  const hidden = matches.filter(m => !m.is_live && m.item_id !== ed.picked);
  const visible = showAll ? matches : matches.filter(m => m.is_live || m.item_id === ed.picked);
  const atts = item.attachments || [];
  const attSel = new Set(ed.attSel);
  return (
    <>
      {item.summary && <div className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1">{item.summary}</div>}
      {reasons.length > 0 && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1 space-y-0.5" data-testid="queue-blockers">
          {reasons.map((r, i) => <div key={i}>⚠ {r.raw || r.label}</div>)}
        </div>
      )}
      {matches.length > 0 && (
        <div>
          <div className="text-[10px] font-medium text-slate-600 mb-0.5">Application ({live.length} live of {matches.length})</div>
          <div className="border rounded divide-y max-h-40 overflow-y-auto">
            {visible.map((m, i) => (
              <label key={m.item_id || i} className="flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer hover:bg-slate-50">
                <input type="radio" name={`pick-${item.queue_id}`} checked={ed.picked === m.item_id} onChange={() => setEd({ picked: m.item_id })} data-testid={`queue-pick-${m.sf}`} />
                <span className="font-mono font-semibold">{m.sf}</span>
                <span className="truncate flex-1">{m.name}</span>
                {m.is_live && <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-300">LIVE</Badge>}
                <Badge variant="outline" className="text-[9px]">{m.status || "—"}</Badge>
              </label>
            ))}
          </div>
          {hidden.length > 0 && !showAll && (
            <button type="button" className="text-[10px] text-slate-500 hover:underline mt-0.5" onClick={() => setShowAll(true)} data-testid={`queue-show-more-${item.queue_id}`}>
              show {hidden.length} more (declined / closed)
            </button>
          )}
        </div>
      )}
      {item.action === "post_update" && (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`queue-status-row-${item.queue_id}`}>
          <span className="text-[10px] font-medium text-slate-600">Status on approve:</span>
          <StatusSelect value={ed.setStatus} onChange={(v) => setEd({ setStatus: v })} testId={`queue-status-select-${item.queue_id}`} />
          {item.payload?.suggested_status && ed.setStatus === item.payload.suggested_status && <span className="text-[10px] text-violet-700" title={item.payload?.status_reasoning || ""}>AI suggestion</span>}
          {item.payload?.suggested_status && ed.setStatus !== item.payload.suggested_status && (
            <button type="button" className="text-[10px] text-violet-700 hover:underline" onClick={() => setEd({ setStatus: item.payload.suggested_status })} title={item.payload?.status_reasoning || ""} data-testid={`queue-use-suggested-${item.queue_id}`}>
              AI suggests: {item.payload.suggested_status}
            </button>
          )}
        </div>
      )}
      {atts.length > 0 && (
        <div>
          <div className="text-[10px] font-medium text-slate-600 mb-0.5"><Paperclip className="w-2.5 h-2.5 inline mr-0.5" />{attSel.size}/{atts.length} files will be attached</div>
          <div className="border rounded divide-y max-h-28 overflow-y-auto">
            {atts.map(a => (
              <label key={a.id} className="flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={attSel.has(a.id)}
                  onChange={() => { const n = new Set(attSel); n.has(a.id) ? n.delete(a.id) : n.add(a.id); setEd({ attSel: [...n] }); }} />
                <span className={cn("truncate flex-1", a.is_zip && "text-slate-400")}>{a.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{fmtKb(a.size)}</span>
                <AttachmentActionIcon messageId={item.message_id} att={a} onPreview={setPreviewAtt} />
              </label>
            ))}
          </div>
        </div>
      )}
      {previewAtt && <AttachmentPreviewDialog messageId={item.message_id} att={previewAtt} onClose={() => setPreviewAtt(null)} />}
    </>
  );
}

function RecentActivity({ done }) {
  if (!done.length) return <div className="p-6 text-center text-xs text-muted-foreground" data-testid="queue-recent-empty">No recent activity.</div>;
  return (
    <div className="divide-y">
      {done.map(q => (
        <div key={q.queue_id} className="px-3 py-2 flex items-start gap-2 text-[11px]" data-testid={`queue-done-${q.queue_id}`}>
          <Badge variant="outline" className={cn("text-[9px] shrink-0", STATUS_TONE[q.status])}>{q.status}</Badge>
          <div className="min-w-0 flex-1">
            <div className="truncate">{q.detail || q.subject}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              {q.subject} · {q.approved_by || q.rejected_by || ""} {q.executed_at ? `· ${new Date(q.executed_at).toLocaleString("en-GB")}` : ""}
            </div>
          </div>
          {q.status === "executed" && q.result?.ack?.sent && <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-300 shrink-0" title={`Acknowledgement sent to ${q.result.ack.to}`} data-testid="queue-ack-sent">Ack sent ✓</Badge>}
          {q.status === "executed" && q.action === "post_update" && <ApplySuggestedBtn item={q} />}
          {q.result?.item_url && <a href={q.result.item_url} target="_blank" rel="noreferrer" className="text-blue-600 shrink-0"><ExternalLink className="w-3 h-3" /></a>}
        </div>
      ))}
    </div>
  );
}
